import { Component, DestroyRef, Inject, inject, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';
import { SecurityActions } from './store';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import {
    AuthenticationResult,
    EventMessage,
    EventType,
    IdTokenClaims,
    InteractionStatus,
    InteractionType,
    PopupRequest,
    RedirectRequest,
} from '@azure/msal-browser';
import { filter, take } from 'rxjs/operators';
import { b2cPolicies } from './auth.config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
    acr?: string;
    tfp?: string;
};

@Component({
    standalone: true,
    imports: [RouterModule, NgxUiLoaderModule],
    selector: 'acorn-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
    #store = inject(Store);
    #destroyRef = inject(DestroyRef);
    public resetPassword: boolean = false;

    constructor(
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService
    ) {}

    ngOnInit(): void {
        this.authService.handleRedirectObservable().subscribe({
            next: (result: AuthenticationResult | null) => {
                if (result) {
                    this.authService.instance.setActiveAccount(result.account);
                }
            },
            error: (error) => {
                this.resetPassword = true;

                if (error?.errorMessage?.includes('AADB2C90118')) {
                    this.redirectToResetPassword();
                } else {
                    console.error('Redirect error:', error);
                }
            },
        });

        this.authService.instance.enableAccountStorageEvents();
        this.#broadcastListen();
    }

    // login(userFlowRequest?: RedirectRequest | PopupRequest) {
    //   if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
    //     if (this.msalGuardConfig.authRequest) {
    //       this.authService
    //         .loginPopup({
    //           ...this.msalGuardConfig.authRequest,
    //           ...userFlowRequest,
    //         } as PopupRequest)
    //         .subscribe((response: AuthenticationResult) => {
    //           this.authService.instance.setActiveAccount(response.account);
    //         });
    //     } else {
    //       this.authService
    //         .loginPopup(userFlowRequest)
    //         .subscribe((response: AuthenticationResult) => {
    //           this.authService.instance.setActiveAccount(response.account);
    //         });
    //     }
    //   } else {
    //     if (this.msalGuardConfig.authRequest) {
    //       this.authService.loginRedirect({
    //         ...this.msalGuardConfig.authRequest,
    //         ...userFlowRequest,
    //       } as RedirectRequest);
    //     } else {
    //       this.authService.loginRedirect(userFlowRequest);
    //     }
    //   }
    // }

    login(userFlowRequest?: RedirectRequest | PopupRequest) {
        if (this.resetPassword) return;

        const request = {
            ...this.msalGuardConfig.authRequest,
            ...userFlowRequest,
        };

        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            this.authService.loginPopup(request as PopupRequest).subscribe({
                next: (response: AuthenticationResult) => {
                    this.authService.instance.setActiveAccount(response.account);
                },
                error: (error) => {
                    this.resetPassword = true;
                    if (error?.errorMessage?.includes('AADB2C90118')) {
                        this.redirectToResetPassword();
                    } else {
                        console.error('Popup login error:', error);
                    }
                },
            });
        } else {
            this.authService.loginRedirect(request as RedirectRequest);
        }
    }

    redirectToResetPassword(): void {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status) => status === InteractionStatus.None),
                take(1)
            )
            .subscribe(() => {
                this.authService.loginRedirect({
                    authority: b2cPolicies.authorities.resetPassword.authority,
                    scopes: ['openid'],
                    redirectUri: '/',
                });
            });
    }

    #broadcastListen(): void {
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe((result: EventMessage) => {
                const payload = result.payload as AuthenticationResult;
                const idToken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

                if (idToken.acr === b2cPolicies.names.resetPassword || idToken.tfp === b2cPolicies.names.resetPassword) {
                    this.resetPassword = true;
                    this.authService.logoutRedirect({ postLogoutRedirectUri: '/' });
                } else {
                    this.authService.instance.setActiveAccount(payload.account);
                }
            });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe(() => {
                const activeAccount = this.authService.instance.getActiveAccount();
                const idToken = activeAccount?.idTokenClaims as IdTokenClaimsWithPolicyId;

                const isResetFlow = idToken?.acr === b2cPolicies.names.resetPassword || idToken?.tfp === b2cPolicies.names.resetPassword;

                if (!isResetFlow) {
                    this.#checkAndSetActiveAccount();
                }
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe((msg: EventMessage) => {
                const error: any = msg.error;
                const errorMessage: string = error?.errorMessage || error?.message || '';

                if (errorMessage.includes('AADB2C90118')) {
                    this.resetPassword = true;
                    this.redirectToResetPassword();
                } else {
                    console.error('Login failure:', error);
                }
            });
    }

    // #broadcastListen(): void {
    //   this.msalBroadcastService.msalSubject$
    //     .pipe(
    //       filter(
    //         (msg: EventMessage) =>
    //           msg.eventType === EventType.ACCOUNT_ADDED ||
    //           msg.eventType === EventType.ACCOUNT_REMOVED
    //       ),
    //       takeUntilDestroyed(this.#destroyRef)
    //     )
    //     .subscribe(() => {
    //       if (this.authService.instance.getAllAccounts().length === 0) {
    //         window.location.pathname = '/';
    //       }
    //     });
  
    //   this.msalBroadcastService.inProgress$
    //     .pipe(
    //       filter(
    //         (status: InteractionStatus) => status === InteractionStatus.None
    //       ),
    //       takeUntilDestroyed(this.#destroyRef)
    //     )
    //     .subscribe(() => {
    //       this.#checkAndSetActiveAccount();
    //     });
  
    //   this.msalBroadcastService.msalSubject$
    //     .pipe(
    //       filter(
    //         (msg: EventMessage) =>
    //           msg.eventType === EventType.LOGIN_SUCCESS ||
    //           msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
    //           msg.eventType === EventType.SSO_SILENT_SUCCESS
    //       ),
    //       takeUntilDestroyed(this.#destroyRef)
    //     )
    //     .subscribe((result: EventMessage) => {
    //       const payload = result.payload as AuthenticationResult;
    //       const idToken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;
  
    //       if (
    //         idToken.acr === b2cPolicies.names.signUpSignIn ||
    //         idToken.tfp === b2cPolicies.names.signUpSignIn
    //       ) {
    //         this.authService.instance.setActiveAccount(payload.account);
    //       }
  
    //       return result;
    //     });
    // }

    #checkAndSetActiveAccount() {
        const accounts = this.authService.instance.getAllAccounts();

        if (accounts.length <= 0) {
            this.login();
            return;
        }

        const activeAccount = this.authService.instance.getActiveAccount();

        if (!activeAccount) {
            this.authService.instance.setActiveAccount(accounts[0]);
        }

        this.#verifyActiveAccount();
    }

    #verifyActiveAccount(): void {
        const activeAccount = this.authService.instance.getActiveAccount();
        
        if (!activeAccount) {
          return;
        }

        this.#store.dispatch(
            SecurityActions.signInSso({
                email: activeAccount.username,
                token: activeAccount.idToken || '',
            })
        );
    }
}
